/**
 * Product cards fade to their second image in CSS (birrot.css, Showcase's
 * `.image__second`), so Horizon's own hover swap is turned off.
 *
 * Horizon swaps a card to its second image on pointerenter and back on
 * pointerleave (product-card.js `previewImage` / `resetImage`, wired by
 * `on:pointerenter` in snippets/card-gallery.liquid while
 * `show_second_image_on_hover` is on; the setting stays on, since it is what
 * renders the second image). With the slides stacked, that swap only moved
 * the slideshow's `current` away from the image on show, two slides at a time,
 * and left a card on its second image in the back-forward cache. Showcase has
 * no swap below 1025px either. component.js looks the handler up on the card
 * when the event fires, so cards rendered later are covered too.
 */
customElements.whenDefined('product-card').then(() => {
  const ProductCard = /** @type {any} */ (customElements.get('product-card'));
  ProductCard.prototype.previewImage = () => {};
  ProductCard.prototype.resetImage = () => {};
});

/**
 * A card photo that fails is asked for again, up to three times. Under
 * `theme dev` images go through the CLI's proxy, which drops requests when a
 * fast scroll down a long list asks for many at once; the browser never
 * retries a failed <img>, so the card kept its broken-image box. The CDN
 * rarely fails, so on the store this is a safety net.
 *
 * The retry is a fresh copy of the element in its place: setting the same
 * `src` again fires an `error` of its own on the way. The waits double
 * (1s, 2s, 4s) and each takes up to half as much again at random, so the
 * images one burst dropped do not come back as one burst. A photo that
 * loads starts from zero again (a resize can ask it for a larger rendition).
 * birrot-card-ink.js reads a card again when a retried photo loads.
 */
const ATTEMPTS = 3;

/** @param {Element | EventTarget | null} image @returns {image is HTMLImageElement} */
const isCardImage = (image) => image instanceof HTMLImageElement && !!image.closest('.card-gallery');

/** @param {HTMLImageElement} image */
function retry(image) {
  const attempt = Number(image.dataset.birrotRetry ?? 0) + 1;
  if (attempt > ATTEMPTS) return;
  image.dataset.birrotRetry = String(attempt);
  const wait = 1000 * 2 ** (attempt - 1) * (1 + Math.random() / 2);
  setTimeout(() => {
    // Gone in the meantime: a filter or sort morphed the card away.
    if (!image.isConnected) return;
    image.replaceWith(image.cloneNode());
  }, wait);
}

/**
 * A card photo that has loaded carries `data-birrot-loaded`: birrot.css
 * fades the first photo out on hover only once the second has, and keeps it
 * on show until then (a second photo still loading is an empty box). This
 * script owns the attribute (set on `load`, taken off when the source
 * changes); assets/birrot-ink.js (`loaded`) and assets/birrot-card-ink.js
 * read it as the sign the photo on the page is the one its markup names,
 * assets/birrot-card-photos.js steps to a photo only once it has it, and
 * birrot.css fades on it. A change here is a change in all five.
 */
const LOADED = 'data-birrot-loaded';

/** @param {HTMLImageElement} image */
const isLoaded = (image) => image.complete && image.naturalWidth > 0;

/**
 * Photos whose `src` or `srcset` changed and whose new photo has not loaded:
 * until it does, `complete` can still describe the old one.
 *
 * @type {WeakSet<HTMLImageElement>}
 */
const changing = new WeakSet();

// `error` and `load` do not bubble, hence the capture.
document.addEventListener('error', ({ target }) => isCardImage(target) && retry(target), true);
document.addEventListener(
  'load',
  ({ target }) => {
    if (!isCardImage(target)) return;
    delete target.dataset.birrotRetry;
    changing.delete(target);
    target.setAttribute(LOADED, '');
  },
  true
);

// This script is deferred: a photo that loaded or failed while the page was
// parsed fired its event before the listeners above. A lazy photo is left
// alone: WebKit reports one not yet asked for as `complete` with no size,
// the same as a failed one, and its `error`, if any, reaches the listener.
for (const image of document.querySelectorAll('.card-gallery img')) {
  if (!isCardImage(image)) continue;
  if (isLoaded(image)) image.setAttribute(LOADED, '');
  else if (image.complete && image.currentSrc && image.loading !== 'lazy') retry(image);
}

// A filter or sort morphs the cards in place. A photo whose source it
// changed is not loaded until its `load`; one it left alone stays loaded but
// loses the attribute and fires no `load` again, so it gets it back here.
// Watched on `main`, where every card is (as birrot-card-ink.js does), not
// the whole body: the header's and the dialogs' images are none of its
// business.
new MutationObserver((records) => {
  for (const { target, attributeName } of records) {
    if (!isCardImage(target)) continue;
    if (attributeName !== LOADED) {
      changing.add(target);
      target.removeAttribute(LOADED);
    } else if (!target.hasAttribute(LOADED) && !changing.has(target) && isLoaded(target)) {
      target.setAttribute(LOADED, '');
    }
  }
}).observe(document.querySelector('main') ?? document.body, { subtree: true, attributeFilter: [LOADED, 'src', 'srcset'] });
